<script lang="ts">
  import { onMount } from "svelte";
  import { resizableColumns } from "$lib/columnWidths.svelte";
  import { _ } from "svelte-i18n";
  import AppShell from "$components/AppShell.svelte";
  import { api } from "$lib/api";

  let { params }: { params: { slug: string } } = $props();
  const slug = $derived(params?.slug ?? "");

  type CostRow = {
    product_id: number;
    product_name: string;
    cost_price: number;
    markup_pct: number;
    base_price: number;
  };

  let rows = $state<CostRow[]>([]);
  let loading = $state(true);
  let toast = $state("");
  let edits = $state<Map<number, { cost_price: string; markup_pct: string }>>(new Map());

  async function load() {
    loading = true;
    try {
      rows = await api.get<CostRow[]>(`/t/${slug}/pricing/costs`);
      const m = new Map<number, { cost_price: string; markup_pct: string }>();
      for (const r of rows) {
        m.set(r.product_id, { cost_price: String(r.cost_price), markup_pct: String(r.markup_pct) });
      }
      edits = m;
    } catch (e) { console.error(e); }
    finally { loading = false; }
  }

  function getEdit(productId: number) {
    return edits.get(productId) ?? { cost_price: "0", markup_pct: "0" };
  }

  function computeBase(productId: number): string {
    const e = getEdit(productId);
    const cp = parseFloat(e.cost_price) || 0;
    const mp = parseFloat(e.markup_pct) || 0;
    return (cp * (1 + mp / 100)).toFixed(2);
  }

  function updateEdit(productId: number, field: "cost_price" | "markup_pct", value: string) {
    const cur = getEdit(productId);
    edits = new Map(edits).set(productId, { ...cur, [field]: value });
  }

  async function saveRow(productId: number) {
    const e = getEdit(productId);
    const cost_price = parseFloat(e.cost_price) || 0;
    const markup_pct = parseFloat(e.markup_pct) || 0;
    try {
      await api.patch(`/t/${slug}/pricing/costs/${productId}`, { cost_price, markup_pct });
      await load();
      toast = "Saved.";
      setTimeout(() => (toast = ""), 2000);
    } catch (err) { console.error(err); }
  }

  onMount(() => { if (slug) load(); });
</script>

<AppShell tenantSlug={slug} active="pricing/formation">
  <div class="pf-page">
    <div class="pf-header">
      <h1 class="pf-title">{$_("nav.pricingFormation")}</h1>
      <p class="pf-desc">Set cost prices and markup coefficients. Base price = cost × (1 + markup%).</p>
    </div>

    <div class="info-banner">
      <span class="material-icons-outlined">info</span>
      The base price is used as the default distributor price. Margin statistics will appear on the Dashboard once it is fully implemented.
    </div>

    {#if loading}
      <div class="loading">{$_("common.loading")}</div>
    {:else if rows.length === 0}
      <div class="empty-state">
        <span class="material-icons-outlined empty-icon">calculate</span>
        <p>No products found. Add products first to manage pricing.</p>
      </div>
    {:else}
      <div class="table-wrap">
        <table use:resizableColumns={"price-formation-card"} class="pf-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Cost price</th>
              <th>Markup %</th>
              <th>Base price</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {#each rows as row}
              <tr>
                <td class="prod-cell">{row.product_name}</td>
                <td>
                  <div class="input-wrap">
                    <input type="number" class="cost-input" step="0.01" min="0"
                      value={getEdit(row.product_id).cost_price}
                      oninput={(e) => updateEdit(row.product_id, "cost_price", (e.target as HTMLInputElement).value)}
                      onblur={() => saveRow(row.product_id)} />
                    <span class="unit">€</span>
                  </div>
                </td>
                <td>
                  <div class="input-wrap">
                    <input type="number" class="cost-input" step="0.1" min="0"
                      value={getEdit(row.product_id).markup_pct}
                      oninput={(e) => updateEdit(row.product_id, "markup_pct", (e.target as HTMLInputElement).value)}
                      onblur={() => saveRow(row.product_id)} />
                    <span class="unit">%</span>
                  </div>
                </td>
                <td class="base-price">{computeBase(row.product_id)} €</td>
                <td>
                  <button class="btn-save" onclick={() => saveRow(row.product_id)}>Save</button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
  {#if toast}<div class="toast">{toast}</div>{/if}
</AppShell>

<style>
  .pf-page { display: flex; flex-direction: column; gap: 20px; }
  .pf-header h1 { margin: 0; font-size: 22px; font-weight: 700; }
  .pf-desc { margin: 4px 0 0; font-size: 14px; color: var(--color-text-muted); }
  .info-banner { background: #e3f2fd; border: 1px solid #90caf9; border-radius: 6px; padding: 12px 16px; font-size: 13px; color: #1565c0; display: flex; align-items: flex-start; gap: 10px; }
  .info-banner .material-icons-outlined { flex-shrink: 0; font-size: 18px !important; }
  .loading { padding: 48px; text-align: center; color: var(--color-text-muted); }
  .empty-state { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 64px; color: var(--color-text-muted); }
  .empty-icon { font-size: 48px !important; opacity: 0.3; }
  .table-wrap { background: #fff; border: 1px solid var(--color-divider); border-radius: 8px; overflow: auto; }
  .pf-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .pf-table th { padding: 10px 14px; border-bottom: 1px solid var(--color-divider); background: #fafafa; font-size: 11px; font-weight: 600; color: var(--color-text-muted); text-transform: uppercase; letter-spacing: 0.05em; text-align: left; }
  .pf-table td { padding: 8px 14px; border-bottom: 1px solid var(--color-divider); }
  .pf-table tr:last-child td { border-bottom: none; }
  .prod-cell { font-weight: 500; min-width: 200px; }
  .input-wrap { position: relative; display: inline-flex; align-items: center; }
  .cost-input { width: 100px; padding: 6px 28px 6px 8px; border: 1px solid var(--color-divider); border-radius: 4px; font-size: 13px; font-family: inherit; }
  .cost-input:focus { outline: none; border-color: var(--color-primary); }
  .unit { position: absolute; right: 8px; color: var(--color-text-muted); font-size: 12px; pointer-events: none; }
  .base-price { font-weight: 600; color: var(--color-primary); }
  .btn-save { border: 1px solid var(--color-primary); color: var(--color-primary); background: #fff; padding: 5px 12px; border-radius: 4px; font-size: 12px; cursor: pointer; }
  .btn-save:hover { background: var(--color-primary); color: #fff; }
  .toast { position: fixed; bottom: 24px; right: 24px; background: #323232; color: #fff; padding: 12px 20px; border-radius: 6px; font-size: 13px; z-index: 9999; }
</style>
